import React from "react";

import {
  t,
  businessName,
  itemName,
  customerMobile,
} from "./invoiceTemplate.shared";

import {
  getBusinessInitials,
  resolveBusinessLogo,
  resolveInvoiceSignature,
} from "./InvoiceBrandAssets";

import "./ProfessionalInvoiceTemplate.css";


const ProfessionalInvoiceTemplate = ({
  invoice,
  invoiceNumber,
  businessSnapshot,
  customerName,
  customerSnapshot,
  billingAddress,
  calculatedItems = [],
  totals = {},
  grandTotal = 0,
  paidAmount = 0,
  balanceAmount = 0,
  displayCurrency,
  formatAddress,
  formatDate,
  formatNumber,
  numberToWords,
}) => {

  const businessLogo =
    resolveBusinessLogo(
      businessSnapshot
    );

  const businessInitials =
    getBusinessInitials(
      businessSnapshot
    );

  const {
    mode:
      signatureMode,
    image:
      signatureImage,
    text:
      signatureText,
    style:
      signatureStyle,
  } =
    resolveInvoiceSignature(
      businessSnapshot
    );

  const businessMobile =
    t(
      businessSnapshot?.mobile
    );

  const businessEmail =
    t(
      businessSnapshot?.email
    );

  const businessGstin =
    t(
      businessSnapshot?.gstin ||
      businessSnapshot?.gstNumber
    );

  const businessPan =
    t(
      businessSnapshot?.pan ||
      businessSnapshot?.panNumber
    );

  const customerContact =
    customerMobile(
      customerSnapshot,
      invoice
    );

  const customerGstin =
    t(
      customerSnapshot?.gstin
    );

  const subtotal =
    Number(
      totals?.subtotal ??
      totals?.grossAmount ??
      totals?.itemGrossAmount ??
      grandTotal
    ) || 0;

  return (

    <section className="invoice-professional">

      {/* =========================================================
          TITLE
      ========================================================= */}

      <div className="invoice-professional__page-title">
        Tax Invoice
      </div>


      {/* =========================================================
          BUSINESS HEADER
      ========================================================= */}

      <div className="invoice-professional__business-header">

        <div className="invoice-professional__logo-box">

          {
            businessLogo
              ? (
                  <img
                    src={businessLogo}
                    alt={businessName(
                      businessSnapshot
                    )}
                  />
                )
              : (
                  <div className="invoice-professional__logo-fallback">
                    {
                      businessInitials
                    }
                  </div>
                )
          }

        </div>


        <div className="invoice-professional__business-info">

          <h1>
            {
              businessName(
                businessSnapshot
              )
            }
          </h1>

          {
            businessSnapshot?.legalName &&
            businessSnapshot?.legalName !==
              businessName(
                businessSnapshot
              ) &&
            (
              <div className="invoice-professional__legal-name">
                {businessSnapshot.legalName}
              </div>
            )
          }

          <p>
            {
              formatAddress(
                businessSnapshot?.address ||
                businessSnapshot
              )
            }
          </p>

          <p>
            Phone no.: {businessMobile}
            {"  "}
            Email: {businessEmail}
          </p>

          <p>
            GSTIN: {businessGstin},
            {" "}
            State: {
              t(
                businessSnapshot?.state ||
                businessSnapshot?.address?.state
              )
            }
          </p>

          {
            businessPan !== "-" &&
            (
              <p>
                PAN: {businessPan}
              </p>
            )
          }

        </div>

      </div>


      {/* =========================================================
          BILL TO / INVOICE DETAILS
      ========================================================= */}

      <div className="invoice-professional__party-row">

        <div className="invoice-professional__party-block">

          <div className="invoice-professional__bar">
            Bill To
          </div>

          <div className="invoice-professional__party-content">

            <strong>
              {customerName}
            </strong>

            <p>
              {
                formatAddress(
                  billingAddress
                )
              }
            </p>

            <p>
              Contact No.: {
                customerContact
              }
            </p>

            {
              customerGstin !== "-" &&
              (
                <p>
                  GSTIN: {
                    customerGstin
                  }
                </p>
              )
            }

          </div>

        </div>


        <div className="invoice-professional__invoice-detail-block">

          <div className="invoice-professional__bar invoice-professional__bar--right">
            Invoice Details
          </div>

          <div className="invoice-professional__detail-row">
            <span>
              Invoice No.:
            </span>

            <strong>
              {invoiceNumber}
            </strong>
          </div>

          <div className="invoice-professional__detail-row">
            <span>
              Date:
            </span>

            <strong>
              {
                formatDate(
                  invoice?.documentDate
                )
              }
            </strong>
          </div>

        </div>

      </div>


      {/* =========================================================
          ITEMS TABLE
      ========================================================= */}

      <table className="invoice-professional__items-table">

        <thead>

          <tr>
            <th className="invoice-professional__col-sno">
              #
            </th>

            <th>
              Item name
            </th>

            <th className="invoice-professional__col-hsn">
              HSN/ SAC
            </th>

            <th className="invoice-professional__col-qty">
              Quantity
            </th>

            <th className="invoice-professional__col-rate">
              Price/ Unit
            </th>

            <th className="invoice-professional__col-amount">
              Amount
            </th>
          </tr>

        </thead>


        <tbody>

          {
            calculatedItems.map(
              (
                item,
                index
              ) => (

                <tr
                  key={
                    item?._id ||
                    item?.id ||
                    index
                  }
                >

                  <td>
                    {index + 1}
                  </td>

                  <td className="invoice-professional__item-name">
                    {
                      itemName(
                        item
                      )
                    }
                  </td>

                  <td>
                    {
                      t(
                        item?.hsnSacCode ||
                        item?.hsnCode
                      )
                    }
                  </td>

                  <td className="invoice-professional__numeric">
                    {
                      formatNumber(
                        item?.calculation?.quantity,
                        0,
                        2
                      )
                    }
                  </td>

                  <td className="invoice-professional__numeric">
                    {
                      displayCurrency(
                        item?.calculation?.rate
                      )
                    }
                  </td>

                  <td className="invoice-professional__numeric">
                    {
                      displayCurrency(
                        item?.calculation?.totalAmount
                      )
                    }
                  </td>

                </tr>

              )
            )
          }


          {/* TOTAL ROW */}

          <tr className="invoice-professional__total-row">

            <td />

            <td>
              Total
            </td>

            <td />

            <td className="invoice-professional__numeric">
              {
                formatNumber(
                  calculatedItems.reduce(
                    (
                      sum,
                      item
                    ) =>
                      sum +
                      Number(
                        item?.calculation?.quantity ||
                        0
                      ),
                    0
                  ),
                  0,
                  2
                )
              }
            </td>

            <td />

            <td className="invoice-professional__numeric">
              {
                displayCurrency(
                  grandTotal
                )
              }
            </td>

          </tr>

        </tbody>

      </table>


      {/* =========================================================
          AMOUNT IN WORDS / AMOUNTS
      ========================================================= */}

      <div className="invoice-professional__amounts-section">

        <div className="invoice-professional__words-block">

          <div className="invoice-professional__bar invoice-professional__bar--center">
            Invoice Amount In Words
          </div>

          <div className="invoice-professional__words-content">
            {
              numberToWords(
                grandTotal
              )
            }
            {" "}
            Rupees only
          </div>

        </div>


        <div className="invoice-professional__summary-block">

          <div className="invoice-professional__bar">
            Amounts
          </div>

          <div className="invoice-professional__summary-row">
            <span>
              Sub Total
            </span>

            <strong>
              {
                displayCurrency(
                  subtotal
                )
              }
            </strong>
          </div>

          <div className="invoice-professional__summary-row invoice-professional__summary-row--bold">
            <span>
              Total
            </span>

            <strong>
              {
                displayCurrency(
                  grandTotal
                )
              }
            </strong>
          </div>

          <div className="invoice-professional__summary-row">
            <span>
              Received
            </span>

            <strong>
              {
                displayCurrency(
                  paidAmount
                )
              }
            </strong>
          </div>

          <div className="invoice-professional__summary-row">
            <span>
              Balance
            </span>

            <strong>
              {
                displayCurrency(
                  balanceAmount
                )
              }
            </strong>
          </div>

        </div>

      </div>


      {/* =========================================================
          BANK / TERMS / SIGNATURE
      ========================================================= */}

      <div className="invoice-professional__footer-grid">

        <div className="invoice-professional__bank-block">

          <div className="invoice-professional__bar">
            Bank Details
          </div>

          <div className="invoice-professional__footer-content">

            <p>
              Name : {
                t(
                  businessSnapshot?.bankDetails?.bankName ||
                  businessSnapshot?.bankName
                )
              }
            </p>

            <p>
              Account No. : {
                t(
                  businessSnapshot?.bankDetails?.accountNumber ||
                  businessSnapshot?.accountNumber
                )
              }
            </p>

            <p>
              IFSC code : {
                t(
                  businessSnapshot?.bankDetails?.ifscCode ||
                  businessSnapshot?.ifscCode
                )
              }
            </p>

            <p>
              Account holder's name : {
                t(
                  businessSnapshot?.bankDetails?.accountName ||
                  businessSnapshot?.accountName
                )
              }
            </p>

          </div>

        </div>


        <div className="invoice-professional__terms-block">

          <div className="invoice-professional__bar">
            Terms and Conditions
          </div>

          <div className="invoice-professional__footer-content">
            {
              invoice?.termsAndConditions ||
              "Thanks for doing business with us!"
            }
          </div>

        </div>


        <div className="invoice-professional__signature-block">

          <div className="invoice-professional__signature-for">
            For : {
              businessName(
                businessSnapshot
              )
            }
          </div>

          {
            (
              signatureMode ===
              "UPLOAD" ||
              signatureMode ===
              "DRAW"
            ) &&
            signatureImage &&
            (
              <img
                className="invoice-professional__signature-image"
                src={signatureImage}
                alt="Authorized Signature"
              />
            )
          }

          {
            signatureMode ===
            "TEXT" &&
            signatureText &&
            (
              <div
                className={[
                  "invoice-professional__typed-signature",
                  `invoice-professional__typed-signature--${String(
                    signatureStyle
                  ).toLowerCase()}`,
                ].join(
                  " "
                )}
              >
                {signatureText}
              </div>
            )
          }

          <strong>
            Authorized Signatory
          </strong>

        </div>

      </div>

    </section>

  );

};


export default ProfessionalInvoiceTemplate;
